import { ApiBaseService } from 'api/apibase.service';
import { Cart } from 'api/cart/types/cart-retrieval.type';
import { CartRecipientContact } from 'api/cart/types/cart-contact.type';
import {
  CouponCode,
  CouponCodeRemove,
} from 'api/cart/types/cart-coupon-code-type';
import { CouponCodeState } from 'components/Cart/feature/selectors';
import { Locale } from 'enums/locale';
import { MarketplaceOrderTrackResponse } from 'api/cart/types/cart-mp-tracking.type';
import { TippingState } from 'api/cart/types/cart-tipping.type';
import { getCartNgUrl } from 'utils/cart-validation/cart-region-validation';
import { getMpTrackingUrl } from 'utils/getMpTrackingUrl';
import Cookies from 'js-cookie';
import axios from 'axios';

class CartApiService extends ApiBaseService {
  cartRetrieval(
    cartrId: number,
    userId: number,
    region: number,
  ): Promise<Cart> {
    const url = getCartNgUrl(region);
    const locale =
      Cookies.get('locale') === Locale.FR_CA ? Locale.FR_CA : Locale.EN_US;
    if (url)
      return axios
        .get<Cart>(
          `${url}/cart/retrieval/${cartrId}/${userId}?culture=${locale}`,
        )
        .then(({ data }) => data);
    throw new Error('Invalid URL for CartNG');
  }

  cartEditParentQuantity(
    cartItemId: number,
    quantity: number,
    callCenterUserId: number,
    userId: number,
    region: number,
  ): Promise<Cart> {
    const url = getCartNgUrl(region);
    if (url)
      return axios
        .post<Cart>(
          `${url}/cart/edit/quantity/${cartItemId}/${quantity}/${callCenterUserId}/${userId}`,
        )
        .then(({ data }) => data);
    throw new Error('Invalid URL for CartNG');
  }

  cartEditChildQuantity(
    cartItemId: number,
    quantity: number,
    callCenterUserId: number,
    userId: number,
    region: number,
  ): Promise<Cart> {
    const url = getCartNgUrl(region);
    if (url)
      return axios
        .post<Cart>(
          `${url}/cart/edit/quantitySubItem/${cartItemId}/${quantity}/${callCenterUserId}/${userId}`,
        )
        .then(({ data }) => data);
    throw new Error('Invalid URL for CartNG');
  }

  cartEditMarketplaceQuantity(
    cartItemId: number,
    quantity: number,
    userId: number,
    region: number,
  ): Promise<Cart> {
    const url = getCartNgUrl(region);
    if (url)
      return axios
        .post<Cart>(
          `${url}/cart/edit/marketplace/quantity/${cartItemId}/${quantity}/${userId}`,
        )
        .then(({ data }) => data);
    throw new Error('Invalid URL for CartNG');
  }

  cartRemoveMarketplaceItem(
    cartItemId: number,
    userId: number,
    region: number,
  ): Promise<Cart> {
    const url = getCartNgUrl(region);
    if (url)
      return axios
        .post<Cart>(
          `${url}/cart/edit/marketplace/removeItem/${cartItemId}/${userId}`,
        )
        .then(({ data }) => data);
    throw new Error('Invalid URL for CartNG');
  }

  cartUpdateExpressCheckout(
    cartItemId: number,
    isSelected: boolean,
    userId: number,
    region: number,
  ): Promise<Cart> {
    const url = getCartNgUrl(region);
    if (url)
      return axios
        .post<Cart>(
          `${url}/cart/edit/update-express-checkout/${cartItemId}/${String(
            isSelected,
          )}/${userId}`,
        )
        .then(({ data }) => data);
    throw new Error('Invalid URL for CartNG');
  }

  cartRemove(
    cartItemId: number,
    userId: number,
    region: number,
  ): Promise<Cart> {
    const url = getCartNgUrl(region);
    if (url)
      return axios
        .post<Cart>(`${url}/cart/edit/removeItem/${cartItemId}/${userId}`)
        .then(({ data }) => data);
    throw new Error('Invalid URL for CartNG');
  }

  cartRemoveChild(
    cartItemId: number,
    userId: number,
    region: number,
  ): Promise<Cart> {
    const url = getCartNgUrl(region);
    if (url)
      return axios
        .post<Cart>(`${url}/cart/edit/removeSubItem/${cartItemId}/${userId}`)
        .then(({ data }) => data);
    throw new Error('Invalid URL for CartNG');
  }

  cartRemoveRecipient(cartItemId: number, userId: number): Promise<Cart> {
    return this.apibase
      .post<Cart>(`/cart/edit/removeRecipient/${cartItemId}/${userId}`)
      .then(({ data }) => data);
  }

  cartEditRecipientContact(recipient: CartRecipientContact): Promise<Cart> {
    const url = getCartNgUrl(recipient.region);
    if (url)
      return axios
        .post<Cart>(`${url}/cart/edit/update-recipient-contact`, recipient)
        .then(({ data }) => data);
    throw new Error('Invalid URL for CartNG');
  }

  cartApplyCouponCode({
    crId,
    isCallCenterQAManager,
    orderCount,
    couponCode,
    userSession,
    callCenterMode,
    region,
  }: CouponCode): Promise<CouponCodeState> {
    const url = getCartNgUrl(region);
    if (url)
      return axios
        .post<CouponCodeState>(`${url}/cart/edit/apply-coupon`, {
          crId,
          isCallCenterQAManager,
          orderCount,
          couponCode,
          userSession,
          callCenterMode,
          region,
        })
        .then(({ data }) => {
          const newData = { ...data };
          newData.crId = crId;
          return newData;
        });
    throw new Error('Invalid URL for CartNG');
  }

  cartRemoveCouponCode({
    crId,
    couponCode,
    region,
  }: CouponCodeRemove): Promise<Cart> {
    const url = getCartNgUrl(region);
    if (url)
      return axios
        .post<Cart>(`${url}/cart/edit/remove-coupon`, {
          crId,
          couponCode,
        })
        .then(({ data }) => data);
    throw new Error('Invalid URL for CartNG');
  }

  async getCartCount(cartId: number, region: number): Promise<number> {
    const url = getCartNgUrl(region);
    if (url) {
      const cartCount = await axios.get(`${url}/cart/cartCount/${cartId}`);
      return cartCount.data as number;
    }
    throw new Error('Invalid URL for CartNG');
  }

  async recommendAddToCartItem(
    cartRecipientId: number,
    productId: number,
    region: number,
  ): Promise<Cart | undefined> {
    const url = getCartNgUrl(region);
    try {
      const addItemResponse = await axios.post<Cart>(
        `${url}/cart/edit/marketplace/add-item`,
        {
          cartRecipientId,
          productId,
        },
      );
      return addItemResponse.data;
    } catch (error) {
      return undefined;
    }
  }

  async fetchMpTrackingItems(
    orderNumber: string,
  ): Promise<MarketplaceOrderTrackResponse> {
    const url = getMpTrackingUrl(orderNumber);
    if (url) {
      const marketplaceTrackingData = await axios.get(
        `${url}/cart/orders/${orderNumber}/track`,
      );
      if (!marketplaceTrackingData.data) {
        throw new Error('Invalid Order Number');
      }
      return marketplaceTrackingData.data as MarketplaceOrderTrackResponse;
    }
    throw new Error('Invalid URL');
  }

  async cartUpdateTip(data: TippingState, region: number): Promise<Cart> {
    const url = getCartNgUrl(region);
    if (url) {
      const tipResponse = await axios.post<Cart>(
        `${url}/cart/edit/update-tip`,
        data,
      );
      return tipResponse.data;
    }
    throw new Error('Invalid URL for CartNG');
  }
}

const cartApiService = new CartApiService();

export { cartApiService };
